import { ref } from 'vue'

import { nanoid } from 'nanoid'
import { defineStore } from 'pinia'

export interface INotificationQueueItem {
  /** Unique id for this queue entry — distinct from the config id it renders. */
  id: string
  /** Id of the config (modal / notification) this entry displays. */
  configId: string
}

/**
 * Builds a Pinia store that shows one item at a time and queues the rest —
 * the shared mechanic behind both the notification modals and the in-app
 * notification toasts. Each surface instantiates its own store (its own
 * `current` + `queue`) so a modal and a toast can be on screen simultaneously.
 */
export function createNotificationQueueStore(storeId: string) {
  return defineStore(storeId, () => {
    const queue = ref<INotificationQueueItem[]>([])
    const current = ref<INotificationQueueItem | null>(null)

    const isPending = (configId: string) =>
      current.value?.configId === configId ||
      queue.value.some((item) => item.configId === configId)

    const enqueue = (configId: string): boolean => {
      if (isPending(configId)) return false

      const item: INotificationQueueItem = { id: nanoid(), configId }
      if (!current.value) {
        current.value = item
      } else {
        queue.value.push(item)
      }
      return true
    }

    const dismissCurrent = () => {
      current.value = null
      const next = queue.value.shift()
      if (next) {
        current.value = next
      }
    }

    return {
      queue,
      current,
      enqueue,
      dismissCurrent,
      isPending,
    }
  })
}
